import { useMemo, useState, type ComponentType, type ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { SearchBar } from "../components/SearchBar";
import {
  Wrench,
  Search,
  ClipboardList,
  Settings,
  StickyNote,
  Printer,
  Droplets,
  Layers,
  Cpu,
  Film,
  Thermometer,
  Wind,
  BookOpen,
  Calculator,
  Bookmark,
  Store,
  Flame,
  ArrowRight,
  Briefcase,
  Wand2,
  GraduationCap,
  Sparkles,
  BarChart3,
  CheckCircle2,
  Mail,
  MessageSquare,
  ScanSearch,
  Camera,
  Image as ImageIcon,
  LayoutTemplate,
} from "lucide-react";
import { issuesData } from "../content";
import { searchIssues } from "../utils/search";
import type { Issue } from "../types";

const categoryTiles = [
  { id: "print-quality", label: "Print Quality", icon: Printer, accent: "text-cyan-300" },
  { id: "white-ink", label: "White Ink", icon: Droplets, accent: "text-slate-200" },
  { id: "adhesion", label: "Adhesion & Powder", icon: Layers, accent: "text-emerald-300" },
  { id: "hardware", label: "Hardware", icon: Cpu, accent: "text-rose-300" },
  { id: "film", label: "Film", icon: Film, accent: "text-fuchsia-300" },
  { id: "heat-press", label: "Heat Press", icon: Thermometer, accent: "text-amber-300" },
  { id: "environment", label: "Environment", icon: Wind, accent: "text-sky-300" },
];

const dashboardTools = [
  {
    to: "/issues",
    label: "Browse Issues",
    icon: Search,
    description: "Search the issue library and jump into likely causes fast.",
  },
  {
    to: "/smart-scan",
    label: "Smart Scan",
    icon: ScanSearch,
    description: "Upload a photo of your issue and get pointed toward likely matches fast.",
    featured: true,
    badge: "NEW",
  },
  {
    to: "/mockup-builder",
    label: "Mockup Builder",
    icon: ImageIcon,
    description: "Preview artwork on garments, test placement, and export clean mockups fast.",
  },
  {
    to: "/gangsheet-builder",
    label: "Gangsheet Builder",
    icon: LayoutTemplate,
    description: "Build custom gangsheets, arrange artwork, and export production-ready layouts fast.",
  },
  {
    to: "/color-knockout",
    label: "Color Knockout",
    icon: Wand2,
    description: "Remove backgrounds, clean edges, and prep artwork for DTF with precision controls.",
    featured: true,
    badge: "NEW",
  },
  {
    to: "/spot-wizard",
    label: "DTF Spot Wizard",
    icon: Wand2,
    description: "Download the Windows desktop tool for Maintop-ready CMYK + W1 TIFF output.",
    featured: true,
    badge: "DESKTOP",
  },
  {
    to: "/cards",
    label: "Diagnostic Cards",
    icon: ClipboardList,
    description: "Use fast-reference cards for common failures and fixes.",
  },
  {
    to: "/maintenance",
    label: "Maintenance",
    icon: Settings,
    description: "Stay ahead of clogs, wear, and recurring printer problems.",
  },
  {
    to: "/knowledge-test",
    label: "Knowledge Test",
    icon: GraduationCap,
    description: "Test your DTF skill level and see where you need work.",
  },
  {
    to: "/howto",
    label: "How-To Guides",
    icon: BookOpen,
    description: "Step-by-step walkthroughs for prep, printing, and pressing.",
  },
  {
    to: "/calculator",
    label: "Calculator",
    icon: Calculator,
    description: "Estimate pricing, cost, and job-level production numbers.",
  },
  {
    to: "/business-builder",
    label: "Business Builder",
    icon: Briefcase,
    description: "Level up your pricing, workflow, and shop growth systems.",
  },
  {
    to: "/artwork-prep-actions",
    label: "Artwork Prep Toolkit",
    icon: Wand2,
    description: "Download actions, charts, and printable workflow tools.",
  },
  {
    to: "/glossary",
    label: "Glossary",
    icon: BookOpen,
    description: "Quick definitions for DTF terms, parts, and print language.",
  },
  {
    to: "/vendors",
    label: "Vendors",
    icon: Store,
    description: "Keep trusted suppliers and shop resources in one place.",
  },
  {
    to: "/bookmarks",
    label: "Saved Fixes",
    icon: Bookmark,
    description: "Come back to important issues, guides, and troubleshooting.",
  },
  {
    to: "/notes",
    label: "My Notes",
    icon: StickyNote,
    description: "Save your process notes, settings, and repeat problem logs.",
  },
];

const mostCommonProblemIds = [
  "white-not-printing",
  "film-curling",
  "banding-horizontal",
  "powder-not-sticking",
  "ink-starvation",
  "clogged-head",
];

function severityClass(severity: Issue["severity"]) {
  switch (severity) {
    case "critical":
      return "border-red-400/20 bg-red-500/10 text-red-300";
    case "high":
      return "border-orange-400/20 bg-orange-500/10 text-orange-300";
    case "medium":
      return "border-amber-400/20 bg-amber-500/10 text-amber-300";
    default:
      return "border-emerald-400/20 bg-emerald-500/10 text-emerald-300";
  }
}

function BluePanel({
  children,
  className = "",
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={[
        "relative overflow-hidden rounded-[28px] border border-dtf-accent/20",
        "bg-[radial-gradient(circle_at_top_center,rgba(37,99,235,0.18),transparent_42%),linear-gradient(180deg,rgba(7,16,35,0.98)_0%,rgba(6,14,30,0.98)_100%)]",
        "shadow-[0_0_0_1px_rgba(245,158,11,0.05),0_0_34px_rgba(37,99,235,0.16)]",
        className,
      ].join(" ")}
    >
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(90deg,rgba(37,99,235,0.08),transparent_30%,transparent_70%,rgba(37,99,235,0.06))]" />
      <div className="pointer-events-none absolute inset-0 rounded-[28px] ring-1 ring-inset ring-white/5" />
      {children}
    </div>
  );
}

function SectionTitle({
  icon: Icon,
  eyebrow,
  title,
  description,
}: {
  icon: ComponentType<{ className?: string }>;
  eyebrow: string;
  title: string;
  description?: string;
}) {
  return (
    <div className="mb-6">
      <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-dtf-blue/25 bg-dtf-blue/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.22em] text-dtf-blue">
        <Icon className="h-4 w-4" />
        {eyebrow}
      </div>

      <h2 className="text-2xl font-extrabold tracking-tight text-white md:text-3xl">
        {title}
      </h2>

      {description ? (
        <p className="mt-3 max-w-3xl text-sm leading-6 text-dtf-text-muted md:text-base">
          {description}
        </p>
      ) : null}
    </div>
  );
}

function StatTile({
  value,
  label,
}: {
  value: string;
  label: string;
}) {
  return (
    <div className="rounded-2xl border border-white/10 bg-black/20 p-5 shadow-[0_0_18px_rgba(37,99,235,0.08)]">
      <div className="text-3xl font-black text-dtf-blue">{value}</div>
      <div className="mt-2 text-sm text-dtf-text-muted">{label}</div>
    </div>
  );
}

export function Home() {
  const [searchResults, setSearchResults] = useState<Issue[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  const navigate = useNavigate();

  const handleSearch = (query: string) => {
    setHasSearched(true);

    if (query.trim()) {
      const results = searchIssues(issuesData.issues, query);
      setSearchResults(results);
    } else {
      setSearchResults([]);
    }
  };

  const handleTileNavigation = () => {
    window.scrollTo(0, 0);
    window.setTimeout(() => {
      window.scrollTo(0, 0);
    }, 0);
  };

  const handleProgrammaticNavigation = (to: string) => {
    const active = document.activeElement;
    if (active instanceof HTMLElement) {
      active.blur();
    }

    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;

    navigate(to);

    window.requestAnimationFrame(() => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    });
  };

  const mostCommonProblems = useMemo(
    () =>
      mostCommonProblemIds
        .map((id) => issuesData.issues.find((issue) => issue.id === id))
        .filter(Boolean) as Issue[],
    []
  );

  return (
    <div className="min-h-screen bg-[#070b14] text-white">
      <div className="mx-auto max-w-7xl px-4 py-8 md:px-6 md:py-12">
        {/* HERO */}
        <section className="relative">
          <BluePanel className="p-6 md:p-10">
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_center,rgba(59,130,246,0.26),transparent_46%),radial-gradient(circle_at_bottom_right,rgba(59,130,246,0.10),transparent_28%)]" />

            <div className="relative grid gap-8 lg:grid-cols-[1.15fr_0.85fr] lg:items-center">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full border border-dtf-blue/25 bg-dtf-blue/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.28em] text-dtf-blue">
                  <Sparkles className="h-4 w-4" />
                  DTF Wizard
                </div>

                <h1 className="mt-5 text-4xl font-black tracking-tight text-white md:text-5xl">
                  Diagnose Faster. Fix Smarter. Build Real DTF Confidence.
                </h1>

                <p className="mt-4 max-w-2xl text-sm leading-7 text-dtf-text md:text-base">
                  Professional troubleshooting for DTF print shops. Diagnose problems,
                  search likely causes, use Smart Scan for visual issue matching, sharpen
                  your skills, and keep production moving with less guesswork.
                </p>

                <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                  <Link
                    to="/diagnosis"
                    onClick={handleTileNavigation}
                    className="inline-flex items-center justify-center gap-2 rounded-2xl border border-dtf-blue/35 bg-dtf-blue/15 px-5 py-3 text-sm font-semibold text-white transition hover:-translate-y-0.5 hover:border-dtf-blue/60 hover:bg-dtf-blue/20"
                  >
                    <Wrench className="h-4 w-4" />
                    Start Diagnosis
                  </Link>

                  <Link
                    to="/smart-scan"
                    onClick={handleTileNavigation}
                    className="inline-flex items-center justify-center gap-2 rounded-2xl border border-white/10 bg-black/20 px-5 py-3 text-sm font-semibold text-white transition hover:-translate-y-0.5 hover:border-dtf-blue/30 hover:bg-black/30"
                  >
                    <ScanSearch className="h-4 w-4" />
                    Open Smart Scan
                  </Link>

                  <Link
                    to="/knowledge-test"
                    onClick={handleTileNavigation}
                    className="inline-flex items-center justify-center gap-2 rounded-2xl border border-white/10 bg-black/20 px-5 py-3 text-sm font-semibold text-white transition hover:-translate-y-0.5 hover:border-dtf-blue/30 hover:bg-black/30"
                  >
                    Take Skill Test
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                {[
                  {
                    title: "Need a quick result?",
                    description: "Start with Diagnosis to narrow down likely causes fast.",
                  },
                  {
                    title: "Have a visual problem?",
                    description: "Use Smart Scan to upload a photo and get likely issue matches.",
                  },
                  {
                    title: "Need prep tools?",
                    description: "Open the Artwork Prep Toolkit for actions and downloads.",
                  },
                  {
                    title: "Need shop mockups?",
                    description: "Use Mockup Builder to preview artwork on garments fast.",
                  },
                ].map((item) => (
                  <div
                    key={item.title}
                    className="rounded-[24px] border border-white/10 bg-black/20 p-5 shadow-[0_0_18px_rgba(37,99,235,0.08)]"
                  >
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5 rounded-xl border border-dtf-blue/20 bg-dtf-blue/10 p-2 shadow-[0_0_12px_rgba(59,130,246,0.18)]">
                        <CheckCircle2 className="h-4 w-4 text-dtf-blue" />
                      </div>

                      <div>
                        <h3 className="text-sm font-bold text-white">{item.title}</h3>
                        <p className="mt-2 text-sm leading-6 text-dtf-text-muted">
                          {item.description}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </BluePanel>
        </section>

        {/* SEARCH */}
        <section className="mt-8">
          <BluePanel className="p-5 md:p-6">
            <SectionTitle
              icon={Search}
              eyebrow="Search"
              title="Find a DTF problem fast"
              description="Search your issue library and jump straight into likely problems, causes, and fixes."
            />

            <div className="max-w-3xl">
              <SearchBar
                placeholder="Search DTF problems..."
                onSearch={handleSearch}
                autoFocus
              />
            </div>

            {hasSearched && searchResults.length > 0 ? (
              <div className="mt-6 max-w-3xl space-y-3">
                {searchResults.slice(0, 5).map((issue) => (
                  <Link
                    key={issue.id}
                    to={`/issues/${issue.id}`}
                    onClick={handleTileNavigation}
                    className="flex items-center justify-between gap-3 rounded-2xl border border-white/10 bg-black/20 p-4 transition hover:border-dtf-blue/30 hover:bg-black/30 hover:shadow-[0_0_18px_rgba(37,99,235,0.10)]"
                  >
                    <div>
                      <p className="font-semibold text-white">{issue.title}</p>
                      <p className="mt-1 text-sm text-dtf-text-muted">
                        {issue.whatItLooksLike}
                      </p>
                    </div>

                    <span
                      className={`shrink-0 rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-[0.14em] ${severityClass(
                        issue.severity
                      )}`}
                    >
                      {issue.severity}
                    </span>
                  </Link>
                ))}

                {searchResults.length > 5 ? (
                  <Link
                    to="/issues"
                    onClick={handleTileNavigation}
                    className="inline-flex items-center gap-2 text-sm font-semibold text-dtf-blue transition hover:text-white"
                  >
                    View all {searchResults.length} results
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                ) : null}
              </div>
            ) : null}
          </BluePanel>
        </section>

        {/* PRIMARY TOOLS */}
        <section className="mt-8 grid gap-6 xl:grid-cols-2">
          <Link to="/diagnosis" onClick={handleTileNavigation} className="group block">
            <BluePanel className="p-6 transition duration-300 hover:-translate-y-0.5 hover:border-dtf-blue/35 md:p-8">
              <div className="flex h-full flex-col gap-6 lg:justify-between">
                <div className="flex items-start gap-4">
                  <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl border border-dtf-blue/20 bg-dtf-blue/10 shadow-[0_0_16px_rgba(59,130,246,0.18)]">
                    <Wrench className="h-8 w-8 text-dtf-blue" />
                  </div>

                  <div>
                    <div className="mb-2 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-dtf-blue">
                      <Flame className="h-4 w-4" />
                      Main Tool
                    </div>

                    <h2 className="text-2xl font-extrabold tracking-tight text-white md:text-3xl">
                      Start Diagnosis
                    </h2>

                    <p className="mt-3 max-w-3xl text-sm leading-7 text-dtf-text md:text-base">
                      Walk through the interactive wizard to narrow down your DTF
                      problem, identify the most likely cause, and get step-by-step
                      fixes fast.
                    </p>
                  </div>
                </div>

                <div className="inline-flex items-center gap-2 rounded-2xl border border-dtf-blue/25 bg-dtf-blue/10 px-5 py-3 text-sm font-semibold text-white transition group-hover:border-dtf-blue/40 group-hover:bg-dtf-blue/15">
                  Open Diagnosis
                  <ArrowRight className="h-4 w-4" />
                </div>
              </div>
            </BluePanel>
          </Link>

          <Link to="/smart-scan" onClick={handleTileNavigation} className="group block">
            <BluePanel className="p-6 transition duration-300 hover:-translate-y-0.5 hover:border-dtf-blue/35 md:p-8">
              <div className="flex h-full flex-col gap-6 lg:justify-between">
                <div className="flex items-start gap-4">
                  <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl border border-dtf-blue/20 bg-dtf-blue/10 shadow-[0_0_16px_rgba(59,130,246,0.18)]">
                    <Camera className="h-8 w-8 text-dtf-blue" />
                  </div>

                  <div>
                    <div className="mb-2 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-dtf-blue">
                      <Sparkles className="h-4 w-4" />
                      Smart Tool
                    </div>

                    <h2 className="text-2xl font-extrabold tracking-tight text-white md:text-3xl">
                      Smart Scan
                    </h2>

                    <p className="mt-3 max-w-3xl text-sm leading-7 text-dtf-text md:text-base">
                      Upload a photo of your print issue and let Smart Scan point you
                      toward likely issue matches so you can move faster into the right
                      fix path.
                    </p>
                  </div>
                </div>

                <div className="inline-flex items-center gap-2 rounded-2xl border border-dtf-blue/25 bg-dtf-blue/10 px-5 py-3 text-sm font-semibold text-white transition group-hover:border-dtf-blue/40 group-hover:bg-dtf-blue/15">
                  Open Smart Scan
                  <ArrowRight className="h-4 w-4" />
                </div>
              </div>
            </BluePanel>
          </Link>
        </section>

        {/* QUICK ACCESS TOOLS */}
        <section className="mt-10">
          <SectionTitle
            icon={Sparkles}
            eyebrow="Tools"
            title="Quick Access Tools"
            description="Jump into the parts of DTF Wizard you use most often."
          />

          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            {dashboardTools.map((tool) => {
              const Icon = tool.icon;
              const isFeatured = Boolean(tool.featured);

              return (
                <button
                  key={tool.label}
                  type="button"
                  onClick={() => handleProgrammaticNavigation(tool.to)}
                  className={`group relative overflow-hidden rounded-[26px] border p-6 text-left transition duration-300 hover:-translate-y-1 ${
                    isFeatured
                      ? "border-dtf-blue/30 bg-[radial-gradient(circle_at_top_center,rgba(37,99,235,0.18),transparent_48%),linear-gradient(180deg,rgba(10,22,46,0.98)_0%,rgba(7,15,31,0.98)_100%)] shadow-[0_0_26px_rgba(37,99,235,0.18)] hover:border-dtf-blue/45"
                      : "border-white/10 bg-[radial-gradient(circle_at_top_center,rgba(37,99,235,0.14),transparent_48%),linear-gradient(180deg,rgba(9,21,44,0.98)_0%,rgba(7,15,31,0.98)_100%)] hover:border-dtf-blue/30 hover:shadow-[0_0_26px_rgba(37,99,235,0.18)]"
                  }`}
                >
                  <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(90deg,rgba(37,99,235,0.06),transparent_35%,transparent_65%,rgba(37,99,235,0.04))]" />

                  {tool.badge ? (
                    <div className="absolute right-4 top-4 rounded-full border border-dtf-accent/20 bg-dtf-accent/10 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-dtf-accent">
                      {tool.badge}
                    </div>
                  ) : null}

                  <div className="relative">
                    <div className="mb-5 flex h-16 w-16 items-center justify-center rounded-2xl border border-dtf-blue/20 bg-dtf-blue/10 shadow-[0_0_16px_rgba(59,130,246,0.14)]">
                      <Icon className="h-8 w-8 text-dtf-blue" />
                    </div>

                    <h3 className="text-xl font-extrabold tracking-tight text-white">
                      {tool.label}
                    </h3>

                    <p className="mt-3 text-sm leading-6 text-dtf-text">
                      {tool.description}
                    </p>

                    <div className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-dtf-blue">
                      Open Tool
                      <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </section>

        {/* MOST COMMON PROBLEMS */}
        <section className="mt-10">
          <SectionTitle
            icon={Flame}
            eyebrow="Popular"
            title="Most Common Problems"
            description="Quick links into common failures users often need help with first."
          />

          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            {mostCommonProblems.map((issue) => (
              <Link
                key={issue.id}
                to={`/issues/${issue.id}`}
                onClick={handleTileNavigation}
                className="group relative overflow-hidden rounded-[26px] border border-white/10 bg-[radial-gradient(circle_at_top_center,rgba(37,99,235,0.14),transparent_48%),linear-gradient(180deg,rgba(9,21,44,0.98)_0%,rgba(7,15,31,0.98)_100%)] p-5 transition duration-300 hover:-translate-y-1 hover:border-dtf-blue/30 hover:shadow-[0_0_26px_rgba(37,99,235,0.18)]"
              >
                <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(90deg,rgba(37,99,235,0.06),transparent_35%,transparent_65%,rgba(37,99,235,0.04))]" />

                <div className="relative">
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="text-lg font-bold text-white transition group-hover:text-dtf-blue">
                      {issue.title}
                    </h3>

                    <span
                      className={`shrink-0 rounded-full border px-3 py-1 text-[10px] font-bold uppercase tracking-[0.16em] ${severityClass(
                        issue.severity
                      )}`}
                    >
                      {issue.severity}
                    </span>
                  </div>

                  <p className="mt-3 text-sm leading-6 text-dtf-text">
                    {issue.whatItLooksLike}
                  </p>

                  <div className="mt-4 flex flex-wrap gap-2">
                    {issue.tags.slice(0, 3).map((tag) => (
                      <span
                        key={tag}
                        className="rounded-full border border-white/10 bg-black/20 px-3 py-1 text-xs text-dtf-text-muted"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* CATEGORIES + STATS */}
        <section className="mt-10 grid gap-6 xl:grid-cols-[1.1fr_0.9fr]">
          <BluePanel className="p-6">
            <SectionTitle
              icon={BookOpen}
              eyebrow="Browse"
              title="Browse by Category"
              description="Jump into the area of the workflow where your problem is most likely happening."
            />

            <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
              {categoryTiles.map((tile) => {
                const Icon = tile.icon;

                return (
                  <Link
                    key={tile.id}
                    to={`/issues?category=${tile.id}`}
                    onClick={handleTileNavigation}
                    className="group rounded-2xl border border-white/10 bg-black/20 p-4 text-center transition hover:-translate-y-0.5 hover:border-dtf-blue/30 hover:bg-black/30"
                  >
                    <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl border border-dtf-blue/15 bg-dtf-blue/10 shadow-[0_0_14px_rgba(59,130,246,0.10)]">
                      <Icon className={`h-6 w-6 ${tile.accent}`} />
                    </div>

                    <p className="mt-3 text-sm font-semibold text-white/90">
                      {tile.label}
                    </p>
                  </Link>
                );
              })}
            </div>
          </BluePanel>

          <BluePanel className="p-6">
            <SectionTitle
              icon={BarChart3}
              eyebrow="Stats"
              title="DTF Wizard Snapshot"
              description="A quick look at the troubleshooting library and reference depth inside the app."
            />

            <div className="grid gap-4 sm:grid-cols-2">
              {[
                {
                  value: String(issuesData.issues.length),
                  label: "Issues Documented",
                },
                {
                  value: "84",
                  label: "Diagnostic Cards",
                },
                {
                  value: "34",
                  label: "Decision Nodes",
                },
                {
                  value: "38",
                  label: "Maintenance Tasks",
                },
              ].map((item) => (
                <StatTile key={item.label} value={item.value} label={item.label} />
              ))}
            </div>
          </BluePanel>
        </section>

        {/* SUPPORT / SUGGESTIONS */}
        <section className="mt-10">
          <BluePanel className="p-6 md:p-8">
            <div className="relative grid gap-6 lg:grid-cols-[1.1fr_0.9fr] lg:items-center">
              <div>
                <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-dtf-blue/25 bg-dtf-blue/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.22em] text-dtf-blue">
                  <MessageSquare className="h-4 w-4" />
                  Feedback
                </div>

                <h2 className="text-2xl font-extrabold tracking-tight text-white md:text-3xl">
                  Have a suggestion or a question?
                </h2>

                <p className="mt-3 max-w-2xl text-sm leading-7 text-dtf-text md:text-base">
                  DTF Wizard is actively being improved. If you have an idea, found
                  something missing, or need help with something inside the app, reach
                  out and let us know.
                </p>

                <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                  <a
                    href="mailto:support@dtfwizardapp.com?subject=DTF%20Wizard%20Question%20or%20Suggestion"
                    className="inline-flex items-center justify-center gap-2 rounded-2xl border border-dtf-blue/35 bg-dtf-blue/15 px-5 py-3 text-sm font-semibold text-white transition hover:-translate-y-0.5 hover:border-dtf-blue/60 hover:bg-dtf-blue/20"
                  >
                    <Mail className="h-4 w-4" />
                    Reach Out
                  </a>

                  <a
                    href="mailto:support@dtfwizardapp.com"
                    className="inline-flex items-center justify-center gap-2 rounded-2xl border border-white/10 bg-black/20 px-5 py-3 text-sm font-semibold text-white transition hover:-translate-y-0.5 hover:border-dtf-blue/30 hover:bg-black/30"
                  >
                    support@dtfwizardapp.com
                  </a>
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                {[
                  "Ask a question about a feature",
                  "Suggest something new for the app",
                  "Report something that needs attention",
                  "Help shape future updates",
                ].map((item) => (
                  <div
                    key={item}
                    className="rounded-[24px] border border-white/10 bg-black/20 p-5 shadow-[0_0_18px_rgba(37,99,235,0.08)]"
                  >
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5 rounded-xl border border-dtf-blue/20 bg-dtf-blue/10 p-2 shadow-[0_0_12px_rgba(59,130,246,0.18)]">
                        <CheckCircle2 className="h-4 w-4 text-dtf-blue" />
                      </div>

                      <p className="text-sm leading-6 text-dtf-text-muted">{item}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </BluePanel>
        </section>
      </div>
    </div>
  );
}